import {
  brand,
  Color,
  colors,
  MOTION,
  RADIUS,
  SIDE_NAV_WIDTH,
  withAlpha,
  Wordmark,
} from "@elyxeer/ui";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { isActive, NAV_ITEMS } from "~/components/shell/navigation";

/**
 * The desktop side rail — the persistent chrome that replaces the phone's tab bar.
 *
 * Hidden below the desktop breakpoint with CSS rather than a `useMediaQuery` branch, so
 * there is no first-paint flicker and no chance of the JS and CSS breakpoints drifting.
 *
 * The lockup lives here on desktop, which is why `TopBar` renders on phones only.
 */
export function SideNav() {
  const { t } = useTranslation();
  const location = useLocation();

  return (
    <Box
      component="nav"
      aria-label={t("nav.primary")}
      data-testid="side-nav"
      sx={{
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        gap: 4,
        position: "sticky",
        top: 0,
        alignSelf: "flex-start",
        width: SIDE_NAV_WIDTH,
        flexShrink: 0,
        height: "100dvh",
        paddingInline: 3,
        paddingBlock: 4,
        borderRight: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box sx={{ paddingInline: 1 }}>
        <Wordmark size={22} />
      </Box>

      <List
        disablePadding
        sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
      >
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const selected = isActive(item.path, location.pathname);

          return (
            <ListItemButton
              key={item.path}
              component={RouterLink}
              to={item.path}
              selected={selected}
              // MUI's `selected` only adds a class. `aria-current` is what actually
              // tells a screen reader which destination is the current page.
              aria-current={selected ? "page" : undefined}
              data-testid={`sidenav.${item.path.replace("/", "")}`}
              sx={{
                borderRadius: `${RADIUS.md}px`,
                paddingBlock: 1.25,
                gap: 0.5,
                transition: `background-color ${MOTION.transition}`,
                // A violet wash rather than the default grey: the selected item should
                // read as brand, and it keeps its own hover state legible.
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: withAlpha(colors[Color.Violet700], 0.12),
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: selected ? "link.main" : "text.muted",
                }}
              >
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t(item.labelKey)}
                slotProps={{
                  primary: {
                    variant: "L1",
                    sx: { color: selected ? "text.primary" : "text.secondary" },
                  },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      {/* The rail has only two destinations; the tagline gives the empty space a job. */}
      <Typography
        variant="OV1"
        sx={{ marginTop: "auto", paddingInline: 1, color: "text.muted" }}
      >
        {brand.tagline}
      </Typography>
    </Box>
  );
}
